import React, { useState, useEffect } from 'react';
import { Clock, Check, AlertCircle, X, RotateCcw, Eye, Hand, Loader } from 'lucide-react';
import { useReservationComms, useCancelCommJob, useRequeueCommJob } from '../../../hooks/useReservationComms';
import JobDetailDrawer from './JobDetailDrawer';
import ResendConfirmModal from './ResendConfirmModal';
import { useToast } from '../../../contexts/ToastContext';

function CommunicationTimeline({ reservationId, onSendClick }) {
  const { communications, isLoading, refetch } = useReservationComms(reservationId);
  const cancelMutation = useCancelCommJob();
  const requeueMutation = useRequeueCommJob();
  const toast = useToast();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [resendJob, setResendJob] = useState(null);

  // Auto-refresh when there are queued jobs
  useEffect(() => {
    const hasQueuedJobs = communications.some(c => c.status === 'queued' || c.status === 'processing');
    if (!hasQueuedJobs) return;

    const interval = setInterval(() => {
      refetch();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [communications, refetch]);

  const formatTimestamp = (dateStr, status) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const diffMinutes = Math.floor(diff / 60000);
    const diffHours = Math.floor(diff / 3600000);
    const diffDays = Math.floor(diff / 86400000);

    let relative = '';
    if (diffMinutes < 1) {
      relative = '(just now)';
    } else if (diffMinutes < 60) {
      relative = `(${diffMinutes}m ago)`;
    } else if (diffHours < 24) {
      relative = `(${diffHours}h ago)`;
    } else {
      relative = `(${diffDays}d ago)`;
    }

    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    const prefix = status === 'queued' || status === 'processing' ? 'Scheduled for' : 'Sent';
    return `${prefix} ${formatted} ${relative}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Check size={16} className="timeline-status-icon" />;
      case 'failed':
        return <AlertCircle size={16} className="timeline-status-icon" />;
      case 'canceled':
        return <X size={16} className="timeline-status-icon" />;
      case 'processing':
        return <Loader size={16} className="timeline-status-icon timeline-icon-spin" />;
      case 'queued':
      default:
        return <Clock size={16} className="timeline-status-icon timeline-icon-pulse" />;
    }
  };

  const countResends = (dedupeKey) => {
    if (!dedupeKey) return 0;
    const baseKey = dedupeKey.replace(/:resend-\d+$/, '');
    return communications.filter(c => c.dedupe_key && c.dedupe_key.startsWith(`${baseKey}:resend-`)).length;
  };

  const isResend = (dedupeKey) => {
    return dedupeKey && /:resend-\d+$/.test(dedupeKey);
  };

  const handleCancel = async (jobId) => {
    if (!window.confirm('Cancel this scheduled email?')) return;

    try {
      await cancelMutation.mutateAsync(jobId);
      toast.success('Email canceled');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel email');
    }
  };

  const handleRequeue = async (jobId) => {
    try {
      await requeueMutation.mutateAsync(jobId);
      toast.success('Email requeued');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to requeue email');
    }
  };

  if (isLoading) {
    return (
      <div className="communication-timeline">
        <div className="timeline-loading">Loading communications...</div>
      </div>
    );
  }

  if (communications.length === 0) {
    return (
      <div className="communication-timeline">
        <div className="timeline-empty">
          <div className="timeline-empty-icon">
            <Clock size={48} />
          </div>
          <p>No communications for this reservation yet.</p>
          {onSendClick && (
            <button className="btn btn-primary" onClick={onSendClick}>
              Send Email
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="communication-timeline">
      <div className="timeline-line"></div>

      {communications.map((comm, idx) => {
        const resendCount = countResends(comm.dedupe_key);
        const isResendJob = isResend(comm.dedupe_key);

        return (
          <div
            key={comm.id}
            className={`timeline-item ${isResendJob ? 'timeline-item-resend' : ''}`}
          >
            <div className={`timeline-dot timeline-dot-${comm.status}`}>
              {getStatusIcon(comm.status)}
            </div>

            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-title">
                  <strong>{comm.template_name || 'Unknown Template'}</strong>
                  {comm.category && (
                    <span className="timeline-category-badge">{comm.category}</span>
                  )}
                  <span className={`timeline-status-badge timeline-status-${comm.status}`}>
                    {comm.status}
                  </span>
                </div>
                <div className="timeline-card-subtitle">
                  {comm.rule_name ? (
                    <span>{comm.rule_name}</span>
                  ) : (
                    <span className="timeline-manual-send">
                      <Hand size={12} /> Manual Send
                    </span>
                  )}
                </div>
              </div>

              <div className="timeline-card-body">
                {isResendJob && (
                  <div className="timeline-resend-label">
                    <RotateCcw size={12} /> Resend of original email
                  </div>
                )}

                <div className="timeline-card-subject">
                  {comm.rendered_subject || comm.subject_tpl}
                </div>

                <div className="timeline-card-recipient">
                  To: {comm.recipient_email || 'No recipient'}
                </div>

                <div className="timeline-card-timestamp">
                  {formatTimestamp(comm.send_at, comm.status)}
                </div>

                {comm.status === 'sent' && comm.logs && comm.logs.length > 0 && (
                  <div className="timeline-delivery-status">
                    {comm.logs.map((log, logIdx) => (
                      <span key={logIdx} className={`timeline-delivery-chip timeline-delivery-${log.status}`}>
                        {log.status}
                      </span>
                    ))}
                  </div>
                )}

                {comm.status === 'failed' && comm.last_error && (
                  <div className="timeline-error-box">
                    <strong>Error:</strong> {comm.last_error}
                    <div className="timeline-attempt-count">
                      Attempt {comm.attempts} of {comm.max_attempts || 3}
                    </div>
                  </div>
                )}

                {resendCount > 0 && !isResendJob && (
                  <div className="timeline-resend-badge">
                    Resent {resendCount}x
                  </div>
                )}
              </div>

              <div className="timeline-card-actions">
                {comm.status === 'sent' && (
                  <button
                    className="timeline-action-btn"
                    onClick={() => setResendJob(comm)}
                    title="Resend"
                  >
                    <RotateCcw size={14} /> Resend
                  </button>
                )}
                {comm.status === 'failed' && (
                  <button
                    className="timeline-action-btn"
                    onClick={() => handleRequeue(comm.id)}
                    title="Requeue"
                  >
                    <RotateCcw size={14} /> Requeue
                  </button>
                )}
                {comm.status === 'queued' && (
                  <button
                    className="timeline-action-btn timeline-action-danger"
                    onClick={() => handleCancel(comm.id)}
                    title="Cancel"
                  >
                    <X size={14} /> Cancel
                  </button>
                )}
                <button
                  className="timeline-action-btn"
                  onClick={() => setSelectedJobId(comm.id)}
                  title="View Details"
                >
                  <Eye size={14} /> Details
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {selectedJobId && (
        <JobDetailDrawer
          jobId={selectedJobId}
          onClose={() => setSelectedJobId(null)}
        />
      )}

      {resendJob && (
        <ResendConfirmModal
          reservationId={reservationId}
          job={resendJob}
          onClose={() => {
            setResendJob(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

export default CommunicationTimeline;
